export const LogLevel = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
    NONE: 4
} as const;
export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

const logLevelNames: Record<number, string> = {
    [LogLevel.DEBUG]: 'DEBUG',
    [LogLevel.INFO]: 'INFO ',
    [LogLevel.WARN]: 'WARN ',
    [LogLevel.ERROR]: 'ERROR',
    [LogLevel.NONE]: 'NONE '
};

class LoggerClass {
    currentLogLevel: LogLevel;
    consoleOutput: boolean;
    logsDirectory: string | null;
    contextLogFiles: Record<string, string>;
    _collectedLogs: string[];
    _collectLogs: boolean;

    constructor() {
        this.currentLogLevel = LogLevel.INFO;
        this.consoleOutput = true;
        this.logsDirectory = null;
        this.contextLogFiles = {};
        this._collectedLogs = [];
        this._collectLogs = false;
    }

    configure(level: LogLevel, consoleOutput: boolean = true): void {
        this.currentLogLevel = level;
        this.consoleOutput = consoleOutput;
    }

    setLogsDirectory(logsDirectory: string): void {
        this.logsDirectory = logsDirectory;
    }

    setLogLevel(level: LogLevel): void {
        this.currentLogLevel = level;
    }

    getLogLevel(): LogLevel {
        return this.currentLogLevel;
    }

    enableLogCollection(): void {
        this._collectLogs = true;
    }

    disableLogCollection(): void {
        this._collectLogs = false;
    }

    getAndClearLogs(): string {
        const logs = this._collectedLogs.join('\n');
        this._collectedLogs = [];
        return logs;
    }

    clearLogs(): void {
        this._collectedLogs = [];
    }

    getLogs(): string {
        return this._collectedLogs.join('\n');
    }

    debug(message: string, context: string | null = null): void {
        this._log(LogLevel.DEBUG, message, context);
    }

    info(message: string, context: string | null = null): void {
        this._log(LogLevel.INFO, message, context);
    }

    warn(message: string, context: string | null = null): void {
        this._log(LogLevel.WARN, message, context);
    }

    error(message: string, context: string | null = null): void {
        this._log(LogLevel.ERROR, message, context);
    }

    // Flow logging is a file-based diagnostic sink (see node logger.ts); it is inert in the browser,
    // mirroring the node default where flow is disabled. Provided so bundle callers that invoke
    // Logger.flow(...) (e.g. RegistryStore.register) do not throw "Logger.flow is not a function".
    flow(_message: string, _context: string): void {
        // no-op in browser
    }

    // Fallback logging mirrors node logger.ts: it routes a formatted [FALLBACK:code] line through the
    // WARN console path. The dedicated fallback.log file sink is inert in the browser. Provided so bundle
    // callers that invoke Logger.fallback(...) (engine/loader fallback paths, e.g. F-ENG1/F-ENG2/F-COE2)
    // do not throw "Logger.fallback is not a function" when a client-side merge hits a fallback path.
    fallback(code: string, message: string, context: string | null = null): void {
        const codeStr = code && code.length > 0 ? code : '?';
        const ctxSuffix = context && context.length > 0 ? ` {${context}}` : '';
        const line = `[FALLBACK:${codeStr}]${ctxSuffix} ${message}`;
        this._log(LogLevel.WARN, line, context);
    }

    _log(level: LogLevel, message: string, context: string | null = null): void {
        if (level < this.currentLogLevel) {
            return;
        }

        const now = new Date();
        const timestamp = now.toISOString().replace('T', ' ').substring(0, 23);
        const levelStr = logLevelNames[level];
        const contextStr = context ? `[${context}] ` : '';
        const logLine = `${timestamp} ${levelStr} ${contextStr}${message}`;

        // Collect logs if enabled
        if (this._collectLogs) {
            this._collectedLogs.push(logLine);
        }

        // Console output
        if (this.consoleOutput) {
            switch (level) {
                case LogLevel.DEBUG:
                    console.debug(logLine);
                    break;
                case LogLevel.INFO:
                    console.info(logLine);
                    break;
                case LogLevel.WARN:
                    console.warn(logLine);
                    break;
                case LogLevel.ERROR:
                    console.error(logLine);
                    break;
                default:
                    console.log(logLine);
                    break;
            }
        }
    }

    clearAllLogs(): void {
        this._collectedLogs = [];
    }

    clearOldLogs(_days: number): void {
        // No-op in browser - file operations not supported
    }

    configureContextLogFiles(contextLogFiles: Record<string, string>): void {
        this.contextLogFiles = { ...contextLogFiles };
        // No-op in browser - file operations not supported
    }

    addContextLogFiles(contextLogFiles: Record<string, string>): void {
        for (const [context, filePath] of Object.entries(contextLogFiles)) {
            this.contextLogFiles[context] = filePath;
        }
        // No-op in browser - file operations not supported
    }

    flush(): void {
        // No-op in browser - console writes are immediate
    }
}

// Export singleton instance
export const Logger = new LoggerClass();

// Export convenience functions (same as Logger.ts)
export const configure = (level: LogLevel, consoleOutput?: boolean): void => Logger.configure(level, consoleOutput);
export const setLogLevel = (level: LogLevel): void => Logger.setLogLevel(level);
export const getLogLevel = (): LogLevel => Logger.getLogLevel();
export const debug = (message: string, context?: string | null): void => Logger.debug(message, context);
export const info = (message: string, context?: string | null): void => Logger.info(message, context);
export const warn = (message: string, context?: string | null): void => Logger.warn(message, context);
export const error = (message: string, context?: string | null): void => Logger.error(message, context);
export const flow = (message: string, context: string): void => Logger.flow(message, context);
export const configureContextLogFiles = (contextLogFiles: Record<string, string>): void => Logger.configureContextLogFiles(contextLogFiles);
export const addContextLogFiles = (contextLogFiles: Record<string, string>): void => Logger.addContextLogFiles(contextLogFiles);
export const flush = (): void => Logger.flush();

// Browser-specific exports
export const enableLogCollection = (): void => Logger.enableLogCollection();
export const disableLogCollection = (): void => Logger.disableLogCollection();
export const getAndClearLogs = (): string => Logger.getAndClearLogs();
export const clearLogs = (): void => Logger.clearLogs();
export const getLogs = (): string => Logger.getLogs();

export default Logger;
